// Is this keystroke meant for the page, or for whatever the author is typing
// into?
//
// Every global shortcut needs the same answer, and the app had two versions of
// it: the slide-clipboard handler tested the element, its editors and any open
// dialog; the block-insert handler tested only the tag name and
// `isContentEditable`. So Ctrl+Alt+<letter> stayed live inside an open modal,
// where it would try to open a second one on top of the first.
//
// One definition, used by both.

/** Elements that are a text field by virtue of being that element. */
const FIELD_TAGS = new Set(['input', 'textarea', 'select']);

/**
 * `<input>` types that hold no text, so no keystroke is ever "being typed into"
 * them.
 *
 * The tag check alone was too blunt. The sections drawer puts a checkbox on
 * every slide row ("pick this slide"), the colour pickers are
 * `<input type="color">`, and clicking any of them leaves it as the focused
 * element — and therefore as `event.target` for the next keypress. Every insert
 * shortcut then quietly refused to fire until focus happened to move somewhere
 * else, which from the outside is a shortcut that works sometimes.
 *
 * A checkbox has its own key: Space. Nothing here answers to a letter, so a
 * modifier chord aimed at the deck cannot be taking one away from them.
 */
const NON_TEXT_INPUT_TYPES = new Set([
  'button', 'checkbox', 'color', 'file', 'image', 'radio', 'range', 'reset', 'submit'
]);

/**
 * Places a keystroke belongs to something other than the deck.
 *
 * - `[contenteditable="true"]` and `.ProseMirror` — the rich-text editors.
 * - `.monaco-editor` — the code block's editor. Monaco puts the caret in a
 *   hidden textarea, so the tag check usually catches it too; the class is
 *   here for the frames where it does not.
 * - `.lp-code-editor` — the Custom HTML/CSS boxes, which are a textarea over a
 *   highlight layer. A click on the highlight layer or the gutter leaves the
 *   event target on a `<div>`, and the shortcut would fire over the author's
 *   markup.
 *
 * Open dialogs are deliberately NOT in this list. "Is a dialog open?" is a
 * question about the screen, not about the focused element — see `isDialogOpen`
 * — and asking it through focus gets the answer wrong in the common case: a
 * dialog that does not claim focus when it opens leaves it on whatever was
 * clicked last, so a focus-based test reports "no dialog" while one is filling
 * the screen.
 */
const EDITOR_CONTAINERS = '[contenteditable="true"], .ProseMirror, .monaco-editor, .lp-code-editor';

/**
 * True when the keystroke is going into a field, an editor or a dialog, and a
 * global shortcut must therefore keep its hands off it.
 *
 * @param {EventTarget|null} target - usually `event.target`
 * @returns {boolean}
 */
export const isTypingTarget = (target) => {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName?.toLowerCase?.();
  if (tag && FIELD_TAGS.has(tag)) {
    // A checkbox, radio or colour swatch is a control, not a field. Anything
    // else with a caret in it — including an `<input>` with no type at all,
    // which is a text box — is.
    const type = tag === 'input' ? (target.type || 'text').toLowerCase() : '';
    if (!NON_TEXT_INPUT_TYPES.has(type)) return true;
    // Still a field for anything else the target sits inside.
    return Boolean(target.closest?.(EDITOR_CONTAINERS));
  }
  return Boolean(target.closest?.(EDITOR_CONTAINERS));
};

/**
 * Is a dialog on screen?
 *
 * Every app dialog carries `data-app-modal`, and three handlers already ask
 * this exact question this exact way — the Slide Designer's copy and paste,
 * and the deck's undo. A global shortcut that acts while a dialog is up acts
 * on something the author cannot see, behind a surface they believe they are
 * working in.
 *
 * It is a document query rather than a walk up from `event.target` because a
 * dialog need not hold focus. The Custom HTML/CSS editor does not take it when
 * it opens, so focus stays wherever it was — on a sidebar button, say — and a
 * test that walks up from there finds no dialog and lets the shortcut through.
 * Measured: with the shortcut list open and focus on a sidebar button,
 * Ctrl+Alt+M opened the block editor on top of it.
 */
export const isDialogOpen = () =>
  typeof document !== 'undefined' && Boolean(document.querySelector('[data-app-modal]'));

export default isTypingTarget;
